/**
 * DocxViewer — render a DOCX in-canvas as selectable HTML using mammoth.
 *
 * Why this exists: the FilePreviewOverlay used to fall back to a "can't
 * preview, open in Word" placeholder for .docx files, which broke the
 * "open BRD → highlight → ask the agent about that part" workflow that
 * users have been asking for. Mammoth converts a docx ArrayBuffer to
 * HTML in the browser (lossy on complex layouts, fine for read+select),
 * letting us render the doc in a `<div>` we control. Standard
 * `window.getSelection()` works on the rendered HTML, which is the
 * foundation for the Phase 2.8 SelectionPopover that pipes selections
 * into the chat agent's request body.
 *
 * Trade-offs:
 *   - Tables and inline formatting render OK; track-changes, complex
 *     headers/footers, embedded charts may not.
 *   - Large docx (~10MB+) take a couple seconds to convert; we show a
 *     loading state during that.
 *   - Mammoth's HTML output is sanitized here before rendering to prevent
 *     any potential XSS from malicious documents.
 */
import React, { useEffect, useRef, useState } from 'react';
import { sanitizeDocxHtml } from '../../utils/sanitizeDocxHtml';

export default function DocxViewer({ url, fileName }) {
  const [html, setHtml] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setHtml('');
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
        const buf = await res.arrayBuffer();
        // Lazy-import so the ~500KB mammoth bundle only loads when a
        // user actually opens a docx — keeps the initial JS chunk small.
        const mammoth = (await import('mammoth')).default || (await import('mammoth'));
        const result = await mammoth.convertToHtml({ arrayBuffer: buf });
        if (!cancelled) {
          const sanitized = sanitizeDocxHtml(result?.value || '');
          setHtml(sanitized);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (loading) {
    return (
      <div className="docx-viewer docx-viewer-loading">
        <div className="docx-viewer-spinner">Rendering {fileName}…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="docx-viewer docx-viewer-error">
        <div>Couldn't render the document in-browser ({error}).</div>
        <a
          className="modal-btn modal-btn-primary"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          download={fileName}
        >Open in Word / Download</a>
      </div>
    );
  }
  return (
    <div
      ref={containerRef}
      className="docx-viewer selectable-doc"
      // mammoth sanitizes its output — no script tags, only standard
      // structural HTML. We render via dangerouslySetInnerHTML so the
      // converted formatting (paragraphs, tables, lists) renders, and
      // standard selection / copy works against it.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
